import { Component, input, inject, effect, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormArray, FormControl } from '@angular/forms';
import { PanelComponent, Register } from '@xanadu/core';
import { Observable, of } from 'rxjs';
import { Node, Prototype, Folder, Content, PrototypeModel, PrototypeTag, Type } from '@xanadu/model';
import { PrototypeCache, TypeCache } from '@xanadu/cache';
import { SafeStyle } from '@angular/platform-browser';
import { moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { RandomHelper, StringHelper } from '@xanadu/common';

@Component({
    selector: 'app-prototype-panel',
    templateUrl: './prototype-panel.component.html',
    styleUrls: ['./prototype-panel.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager,
    host: {
        '[style]': 'hostStyle'
    }
})
@Register('prototype-panel')
export class PrototypePanelComponent extends PanelComponent<Prototype> {
    private readonly randomHelper = inject(RandomHelper);
    private readonly stringHelper = inject(StringHelper);
    private readonly prototypeCache = inject(PrototypeCache);
    private readonly typeCache = inject(TypeCache);

    key = input<string>();

    hostStyle: SafeStyle = 'height: 100%';

    tabPrototype = { caption: 'Prototype', active: true, hidden: false };
    tabMeta = { caption: 'Meta', active: false, hidden: false };

    metaAuto: any = { id: 'model', name: 'Prototype', display: 'name', mandatory: true, alwaysMatch: true };
    metaGraphql: any = { id: 'graphqlType', name: 'GraphQL Type', mandatory: true, info: 'GraphQL interface name, starts with _, can contain letters, numbers and underscores only.', pattern: '^[_][a-zA-Z0-9_]*$', updateOn: 'blur' }; //id can NOT be 'key', it is a reserved word

    formArray: FormArray<any> =new FormArray<any>([]);
    controlKey?: FormControl;
    types?: Map<string, Type>;
    prototypeModel?: PrototypeModel;

    disableKey: boolean = false;
    focus: any = undefined;

    tags: Map<string, PrototypeTag> = new Map<string, PrototypeTag>();

    graphqlType?: string

    protected override get nodeType(): string | undefined {
        return 'aspect';
    }

    constructor() {
        super();

        effect(() => {
            const formGroup = this.formGroup();

            if (formGroup) {
                formGroup.addControl('fields', this.formArray);
            }
        });

        effect(() => {
            const prototype = this.node();
            this.types = this.typeCache.signal();

            if (prototype && this.types) {
                this.graphqlType = prototype.graphqlType;
                this.loadPrototype(prototype, this.model().custome);
            }
        });
    }

    protected override setup(prototype: Prototype): void {
        super.setup(prototype);

        this.tabs = [this.tabPrototype, this.tabMeta];
        this.tabSelected = this.tabPrototype;
    }

    protected override load(prototype: Prototype, form: any): string | undefined {
        super.load(prototype, form);
        return this.copyForm(prototype, form);
    }

    protected override refresh(prototype: Prototype): void {
        super.refresh(prototype);

        this.prototypeCache.update();
    }

    onAddField(tag?: string) {
        if (tag?.length) {
            let entry = this.tags.get(tag);
            if (entry && this.prototypeModel) {
                const field = { key: '__', value: { name: '', type: { input: '' }, tag, info: '' } };
                const group = new FormGroup({});
                this.prototypeModel.fields.push(field);
                entry.add({ id: this.randomHelper.generate(), field, control: new FormControl(tag), group })

                this.formArray.push(group);
                this.formGroup()?.markAsDirty();
                this.formGroup()?.updateValueAndValidity();
            }
        }
    }

    onAddTag() {
        const tag: string = 'New Tag';
        if (!this.tags.has(tag)) {
            this.tags.set(tag, new PrototypeTag(tag));
            this.model().custome[tag] = { expanded: true };
        }
    }

    onDelete(event: any) {
        const tag = this.tags.get(event.value.tag);

        if (tag && this.prototypeModel) {
            const index = this.prototypeModel.fields.findIndex((x: any) => x === event);
            if (index >= 0) {
                this.prototypeModel.fields.splice(index, 1);
                this.formArray.removeAt(index);
                this.formGroup()?.markAsDirty();
                this.formGroup()?.updateValueAndValidity();
            }

            const tagIndex = tag.items.findIndex((x: any) => x.field === event);
            if (tagIndex >= 0) {
                tag.items.splice(tagIndex, 1);
            }
        }
    }

    onDrop(event: any) {
        let moved = false;
        if (event.previousContainer === event.container) {
            if (event.previousIndex != event.currentIndex) {
                moveItemInArray(event.container.data.items, event.previousIndex, event.currentIndex);
                moved = true;
            }
        } else {
            const tagPrevious = event.previousContainer.data;
            const tagCurrent = event.container.data;

            const item = tagPrevious.items[event.previousIndex];
            if (item) {
                item.field.value.tag = tagCurrent.tag;
                (<FormControl>item.control).patchValue(item.field.value.tag);
            }

            transferArrayItem(
                tagPrevious.items,
                tagCurrent.items,
                event.previousIndex,
                event.currentIndex,
            );
            moved = true;
        }

        if (moved) {
            this.formGroup()?.markAsDirty();
            this.formGroup()?.updateValueAndValidity();
        }
    }

    onTag(event: { tag: string, data: PrototypeTag }) {
        const prototypeTag = event.data;
        if (event.tag.startsWith('_')) {
            this.noticeSignal.error('Tag must not start with an underscore.');
            this.focus = prototypeTag;
            return;
        }

        const found = this.tags.get(event.tag);
        if (found && found !== prototypeTag) {
            this.noticeSignal.error('Tag must be unique.');
            this.focus = prototypeTag;
            return;
        }

        this.focus = undefined;

        const value = this.tags.get(prototypeTag.meta.title);
        this.tags.delete(prototypeTag.meta.title);
        const model = this.model();
        const custome = model.custome[prototypeTag.meta.title];
        model.custome[prototypeTag.meta.title] = undefined;
        prototypeTag.setTag(event.tag);
        if (value) {
            this.tags.set(event.tag, value);
        }
        model.custome[event.tag] = custome;

        const items = prototypeTag.items;
        if (items?.length && prototypeTag.meta.title?.length) {
            items.forEach(item => {
                item.field.value.tag = event.tag;
                (<FormControl>item.control).patchValue(item.field.value.tag);
            });

            this.formGroup()?.markAsDirty();
            this.formGroup()?.updateValueAndValidity();
        }
    }

    onFocus() {
        this.disableKey = true;
    }

    onBlur() {
        this.disableKey = false;
    }

    validate: (x: any) => Observable<boolean> = (value) => {
        const prototype = this.node();
        return prototype?._id ? this.dataService.checkGraphQL(prototype._id, value) : of(false);
    }

    private copyForm(prototype: Prototype, form: any): string | undefined {
        const data = {};
        const set = new Set<string>();
        let duplicates: string[] = [];

        const { graphqlType, fields, meta, ...rest } = form;

        const mapFields: Map<string, { name: string, field: any }> = new Map<string, { name: string, field: any }>();

        fields.forEach((field: any)=> {
            if (field.name?.length) {
                const name = this.stringHelper.toLowerCamel(field.name, true);
                if (set.has(name)) {
                    duplicates.push(name);
                } else {
                    if (field.typeId) {
                        const type = this.types?.get(field.typeId)
                        if (type) {
                            field.type = type;
                            field.graphqlType = type.graphqlType;
                        }
                    }
                    mapFields.set(field.id, { name, field });
                    set.add(name);
                }
            }
        });

        this.tags.forEach(tag => {
            if (tag.items?.length) {
                tag.items.forEach((item, index) => {
                    const entry = mapFields.get(item.id);
                    if (entry?.field) {
                        entry.field.index = index;
                        (data as any)[entry.name] = entry.field;
                    }
                });
            }
        });

        if (prototype) {
            prototype.data = data;
            prototype.meta = meta;
            prototype.graphqlType = graphqlType;
        }

        if (duplicates.length) {
            const found = duplicates.join(' ,');
            return `Protytype and Aspect field names have to be unique. Found duplicates: ${found}.`;
        }
    }

    private loadFields(custome: any) {
        this.tags.clear();
        this.formArray.clear();

        if (this.prototypeModel && this.prototypeModel.fields) {
            for (const field of this.prototypeModel.fields) {
                const group = new FormGroup({});
                if (field.value.tag) {
                    let entry = this.tags.get(field.value.tag);
                    if (!entry) {
                        this.tags.set(field.value.tag, entry = new PrototypeTag(field.value.tag));
                        custome[field.value.tag] = custome[field.value.tag] || { expanded: true };
                    }
                    entry.add({ id: this.randomHelper.generate(), field, control: new FormControl(field.value.tag), group })
                }
                this.formArray.push(group);
            }
        }
    }

    private loadPrototype(node: Node, custome: any) {
        let prototype: Prototype | undefined = undefined;
        if (node instanceof Prototype) {
            prototype = node;
        } else if (node instanceof Folder || node instanceof Content) {
            if (node.prototype) {
                prototype = node.prototype;
            }
        }

        if (prototype) {
            this.prototypeModel = new PrototypeModel(prototype);
            this.loadFields(custome);
        }
    }
}
